import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import type {ButtonVariant} from '@pexip/components';
import {NotificationTooltip} from '@pexip/components';
import {skippedStepByStepSignal} from '../signals/StepByStep.signals';
import type {UserMenuStep} from '../types';
import {ConfirmationModalStates} from '../types';
import {useBranding} from '../branding/Context';
import {TopUserMenuDropContent} from '../views/TopUserMenuDropContent/TopUserMenuDropContent.view';
import {TestId} from '../../test/testIds';

import {UserMenuHandler} from './UserMenuHandler.viewModel';
import type {AutoHideUIContextValue} from '../contexts/autoHideUI.context';
import {INITIAL_USER_MENU_STEP} from '../constants';
import {userMenuStepSignal} from '../signals/Menu.signals';

export const UserMenu: React.FC<{
    showLeave?: boolean;
    showQuality?: boolean;
    showMediaStatistics?: boolean;
    autoHideProps?: AutoHideUIContextValue['autoHideProps'];
    toggleAutoHideUI?: AutoHideUIContextValue['toggleAutoHideUI'];
    variant?: ButtonVariant;
    hasMedia?: boolean;
    isInMeeting?: boolean;
    leave?: () => void;
}> = ({
    isInMeeting = false,
    showLeave = false,
    showMediaStatistics,
    showQuality,
    hasMedia = false,
    variant,
    autoHideProps,
    toggleAutoHideUI,
    leave,
}) => {
    const {t} = useTranslation();

    const [
        isTestVideoAndSoundTooltipVisible,
        setIsTestVideoAndSoundTooltipVisible,
    ] = useState(false);

    const [userMenuStep, setUserMenuStep] = useState<UserMenuStep>(
        INITIAL_USER_MENU_STEP,
    );

    const setUserMenuStepAndEmitSignal = useCallback((step: UserMenuStep) => {
        setUserMenuStep(step);
        userMenuStepSignal.emit(step);
    }, []);

    const overlay = useBranding('overlay');

    const [confirmationState, setConfirmationState] = useState(
        ConfirmationModalStates.None,
    );

    const handleBack = useCallback(() => {
        setUserMenuStepAndEmitSignal(INITIAL_USER_MENU_STEP);
    }, [setUserMenuStepAndEmitSignal]);

    useEffect(
        () =>
            skippedStepByStepSignal.add(() => {
                setIsTestVideoAndSoundTooltipVisible(true);
            }),
        [],
    );

    return (
        <NotificationTooltip
            shouldCloseOnOutsideClick
            position="bottomLeft"
            header={t(
                'test-video-and-sound-menu-tooltip.title',
                'Test video and sound',
            )}
            text={t(
                'test-video-and-sound-menu-tooltip.description',
                'You can test your video and sound anytime from the menu',
            )}
            controlledIsVisible={isTestVideoAndSoundTooltipVisible}
            controlledOnChange={setIsTestVideoAndSoundTooltipVisible}
        >
            <TopUserMenuDropContent
                variant={variant}
                dropContentLabel={userMenuStep}
                dropContentRole="dialog"
                canCloseOutside={
                    confirmationState === ConfirmationModalStates.None &&
                    userMenuStep !== 'stats'
                }
                onClose={handleBack}
                colorScheme={overlay}
                data-testid={TestId.UserMenuButton}
                content={close => (
                    <UserMenuHandler
                        showQuality={showQuality}
                        showMediaStatistics={showMediaStatistics}
                        showLeave={showLeave}
                        close={close}
                        onBackClick={handleBack}
                        isInMeeting={isInMeeting}
                        setUserMenuStep={setUserMenuStepAndEmitSignal}
                        userMenuStep={userMenuStep}
                        hasMedia={hasMedia}
                        confirmationState={confirmationState}
                        setConfirmationState={setConfirmationState}
                        leave={leave}
                        {...autoHideProps}
                    />
                )}
                onContentVisibilityChange={isVisible =>
                    toggleAutoHideUI?.('openedUserMenu', isVisible)
                }
                onPointerUp={e => e.stopPropagation()}
                {...autoHideProps}
            />
        </NotificationTooltip>
    );
};
